import React, { useState, useEffect } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../../utils/userErrors';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Eye,
  EyeOff,
  Headphones,
  Lock,
  Shield,
} from 'lucide-react';
import { translations } from '../../utils/translations';
import apiService from '../../utils/apiService';
import { useUser } from '../../contexts/UserContext';
import { invalidateKycVerifiedCache } from '../../hooks/useKycVerified';
import NewtonsCradleLoader from '../common/NewtonsCradleLoader';
import TwoFactorStep from './TwoFactorStep';

const PARTNER_CODE_PREFIX = 'ABT-P-';

const trustFeatures = [
  {
    icon: Shield,
    title: 'Secure & Trusted',
    desc: 'Bank-grade security for your account and earnings.',
  },
  {
    icon: Lock,
    title: 'Private & Confidential',
    desc: 'Your information is safe and never shared.',
  },
  {
    icon: Headphones,
    title: '24/5 Support',
    desc: "We're here to help you succeed anytime.",
  },
];

/** Store only the suffix in state; strip accidental full-code / prefix paste. */
const sanitizePartnerCodeSuffix = (value) => {
  let raw = String(value || '').trim().toUpperCase();
  if (raw.startsWith(PARTNER_CODE_PREFIX)) {
    raw = raw.slice(PARTNER_CODE_PREFIX.length);
  } else if (raw.startsWith('ABT-P')) {
    raw = raw.slice(5).replace(/^-+/, '');
  }
  return raw.replace(/[^A-Z0-9-]/g, '');
};

const toFullPartnerCode = (suffix) => {
  const clean = sanitizePartnerCodeSuffix(suffix);
  return clean ? `${PARTNER_CODE_PREFIX}${clean}` : '';
};

const LoginForm = () => {
  const { updateUserData } = useUser();
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [showPassword, setShowPassword] = useState(false);
  const [partnerCode, setPartnerCode] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Second step (authenticator). Kept in memory only; nothing is persisted until the final success.
  const [twoFactor, setTwoFactor] = useState(null); // { challengeToken, setupRequired, affiliateCode }

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage && translations[savedLanguage]) {
      setSelectedLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail;
      if (newLanguage && translations[newLanguage]) {
        setSelectedLanguage(newLanguage);
      }
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const finishLogin = (response) => {
    invalidateKycVerifiedCache();
    updateUserData(response.data, response.data.userPermissions || []);
    localStorage.setItem('lastLoginTime', Date.now().toString());
    if (rememberMe) localStorage.setItem('rememberMe', 'true');
    window.dispatchEvent(new CustomEvent('loginStateChanged'));
    navigate('/');
  };

  const handleTwoFactorSuccess = (result) => {
    // Same persistence + navigation as a normal login, only now that every step has succeeded.
    const persisted = apiService.persistLoginSession(result, twoFactor?.affiliateCode);
    setTwoFactor(null);
    finishLogin(persisted);
  };

  const handleTwoFactorBack = (message) => {
    setTwoFactor(null);
    setPassword('');
    setError(typeof message === 'string' ? message : '');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const fullAffiliateCode = toFullPartnerCode(partnerCode);
    if (!fullAffiliateCode) {
      setError('Please enter your partner code');
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiService.login(fullAffiliateCode, password);
      if (response.twoFactorRequired && response.challengeToken) {
        setPassword('');
        setTwoFactor({
          challengeToken: response.challengeToken,
          setupRequired: response.setupRequired === true,
          affiliateCode: fullAffiliateCode,
        });
      } else if (response.success && response.data) {
        finishLogin(response);
      } else {
        setError(response.error || response.message || 'Login failed');
      }
    } catch (err) {
      setError(toUserMessage(err, ERROR_MESSAGES.request));
    } finally {
      setIsLoading(false);
    }
  };

  const t = translations[selectedLanguage] || translations.English;

  return (
    <div className="auth-login__stack">
      {twoFactor ? (
        <TwoFactorStep
          challengeToken={twoFactor.challengeToken}
          setupRequired={twoFactor.setupRequired}
          affiliateCode={twoFactor.affiliateCode}
          onBack={handleTwoFactorBack}
          onSuccess={handleTwoFactorSuccess}
        />
      ) : (
      <div className="auth-login__card">
        <div className="auth-login__card-header">
          <h1>Welcome Back!</h1>
          <p>Sign in with your partner code and password</p>
        </div>

        <form onSubmit={handleLogin} className="auth-login__form">
          <div>
            <label htmlFor="partnerCode" className="auth-login__label">
              Partner Code
            </label>
            <div className="auth-login__code-wrap">
              <span className="auth-login__code-prefix" aria-hidden="true">
                {PARTNER_CODE_PREFIX}
              </span>
              <input
                id="partnerCode"
                type="text"
                value={partnerCode}
                onChange={(e) => setPartnerCode(sanitizePartnerCodeSuffix(e.target.value))}
                className="auth-login__code-input"
                placeholder="Enter code"
                autoComplete="username"
                inputMode="text"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="auth-login__label">
              Password
            </label>
            <div className="auth-login__password-wrap">
              <Lock className="auth-login__field-icon" aria-hidden="true" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="auth-login__eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="auth-login__forgot">
              <Link to="/forgot-password">{t.forgotPassword}</Link>
            </div>
          </div>

          <label className="auth-login__remember">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>{t.rememberMe}</span>
          </label>

          {error ? <div className="auth-login__error">{error}</div> : null}

          <button type="submit" disabled={isLoading} className="auth-login__submit">
            {isLoading ? (
              <>
                <NewtonsCradleLoader size="28px" color="#ffffff" />
                <span>{t.signingIn}</span>
              </>
            ) : (
              <>
                Sign In
                <ArrowRight size={16} aria-hidden="true" />
              </>
            )}
          </button>
        </form>

        <p className="auth-login__register">
          {t.noAccount}{' '}
          <Link to="/register">Register Now</Link>
        </p>
      </div>
      )}

      <div className="auth-login__trust">
        {trustFeatures.map(({ icon: Icon, title, desc }) => (
          <div key={title} className="auth-login__trust-item">
            <div className="auth-login__trust-icon">
              <Icon size={18} aria-hidden="true" />
            </div>
            <strong>{title}</strong>
            <p>{desc}</p>
          </div>
        ))}
      </div>

      <p className="auth-login__footer">
        © 2026 Broker Partner. All rights reserved.{' '}
        <button type="button">Terms of Use</button>
        {' - '}
        <button type="button">Privacy Policy</button>
      </p>
    </div>
  );
};

export default LoginForm;
